/**
 * products.js — Portal-side product display metadata.
 *
 * Operational data (id, price, isFree) comes from the API via GET /v1/products.
 * This file adds rich display metadata: tagline, descriptions, features,
 * visual styling, and checkout URLs for paid products.
 *
 * Keyed by product slug for fast lookup.
 */

/**
 * @typedef {object} ProductDisplay
 * @property {string} tagline - Short tagline for the product card
 * @property {string} description - Longer description for the product page
 * @property {string} icon - Lucide icon name (resolved by the component)
 * @property {string[]} highlights - Key feature highlights (3-5 bullets)
 * @property {string} [activationName] - Public product name for activation copy
 * @property {string} badgeLabel - Badge text e.g., "Free", "$19"
 * @property {string} badgeColor - Tailwind color class for the badge
 * @property {string} accent - Tailwind accent color for card border and CTA
 * @property {string} ctaLabel - Button text for the purchase action
 * @property {string | null} checkoutUrl - Polar checkout URL (paid only)
 * @property {string} [moneyBack] - Money-back guarantee text (paid only)
 * @property {string} [extensionId] - Chrome extension ID for one-click activation
 */

/** @type {Record<string, ProductDisplay>} */
export const PRODUCT_DISPLAY = {
    snaply: {
        name: 'Screenshot & OCR Pro',
        activationName: 'Screenshot & OCR',
        tagline: 'Screenshot & OCR for your browser',
        description:
            'Capture any area, annotate with pro tools, extract text with local OCR, and keep repeatable capture workflows moving. Pay once, own forever.',
        icon: 'Camera',
        highlights: [
            'Unlimited OCR extractions + 15+ languages',
            'Unlimited full-page captures and tab comparisons',
            'Unlimited advanced annotations: Blur, Spotlight, QR, labels',
            'Unlimited snapshot history and batch export',
            'Custom watermark, filename templates, and screenshot styling',
            '1 year of updates included',
        ],
        badgeLabel: '$19',
        badgeColor: 'blue',
        accent: 'blue',
        ctaLabel: 'Buy Screenshot & OCR Pro — $19',
        checkoutUrl: import.meta.env.VITE_POLAR_CHECKOUT_URL_SNAPLY || null,
        moneyBack: '14-day money-back guarantee',
        extensionId: import.meta.env.VITE_SNAPLY_EXTENSION_ID || 'nhgljbpjgeijbcdeolafjclcbikkjnpd',
    },
    'url-editor': {
        tagline: 'Power-user URL editing in your browser',
        description:
            'Unlock unlimited presets, regex transforms, cURL export, bulk query actions, and extended history — completely free.',
        icon: 'Link',
        highlights: [
            'Unlimited saved presets',
            'Preset import & export',
            'Regex URL transforms',
            'Extended history (50 items)',
            'Bulk query actions & cURL export',
        ],
        badgeLabel: 'Free',
        badgeColor: 'emerald',
        accent: 'emerald',
        ctaLabel: 'Get Pro — Free',
        checkoutUrl: null,
    },
};

/**
 * Merge API product data with local display metadata.
 * @param {{ id: string, slug: string, name: string, price: number, currency: string, isFree: boolean }[]} apiProducts
 * @returns {Array<{ id: string, slug: string, name: string, price: number, currency: string, isFree: boolean } & ProductDisplay>}
 */
export function enrichProducts(apiProducts) {
    return apiProducts
        .map((p) => {
            const display = PRODUCT_DISPLAY[p.slug];
            if (!display) return null;
            return { ...p, ...display };
        })
        .filter(Boolean);
}

/**
 * Resolve portal display metadata by canonical API product ID.
 * @param {string} productId
 * @returns {ProductDisplay | null}
 */
export function getProductDisplayByProductId(productId) {
    if (!productId) return null;
    return (
        Object.values(PRODUCT_DISPLAY).find((display) => display.productId === productId) ||
        Object.entries(PRODUCT_DISPLAY).find(
            ([slug]) => `hforgex.com/${slug}` === productId
        )?.[1] ||
        null
    );
}
